import { FocusMonitor } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  viewChild,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { convertToSecretCode, MonthlyLabels, QuarterLabels } from './quarter-selector.constants';
import {
  QuarterRangeSelectorState,
  QuarterRangeSelectorValue,
  QuarterSelectorType,
  QuarterSelectorValue,
} from '@miq/shared-models';
import { RangeQuarterSelectorComponent } from './range-quarter-selector.component';
import { RangeQuarterSelectorService } from './range-quarter-selector.service';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
@Component({
  selector: 'miq-quarter-selector',
  styleUrl: 'quarter-selector.component.scss',
  templateUrl: 'quarter-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RangeQuarterSelectorService],
  imports: [NgClass, MatIcon],
})
export class QuarterSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  editable = input(true);
  placeholder = input('');
  type = input(QuarterSelectorType.Quarter);
  readonly valueInput = input<QuarterRangeSelectorValue>(undefined, { alias: 'value' });
  value: QuarterRangeSelectorValue;
  // This value is null explicitly to know whether the user has set the input or not
  singleSelection = input(null);
  requireYear = input(true);
  marketQtdVal = input<QuarterSelectorValue | Date>();
  minValue = input<QuarterSelectorValue | Date>();
  maxValue = input<QuarterSelectorValue | Date>();
  showAsQtd = input(false);
  allowedPeriods = input<QuarterSelectorValue[]>([]);
  restrictedPeriods = input<QuarterSelectorValue[]>([]);
  displayClearButton = input<false>();
  isClearFilterEnabled = input<false>();
  valueChange = output<QuarterRangeSelectorValue>()
  clearFilterEmitter = output<void>()
  readonly wrapperElement = viewChild<ElementRef>('wrapper');
  public displayValue = '';
  public displayValueSet = false;
  public isDialogOpen = false;
  public allowThisPeriodsOnly: Set<number> = null;
  public restrictThisPeriodsOnly: Set<number> = null;
  private minProcessedValue: QuarterSelectorValue;
  private maxProcessedValue: QuarterSelectorValue;
  private readonly defaultMinValue: QuarterSelectorValue = { periodValue: 1, periodYear: 1950 };
  private readonly defaultMaxValue: QuarterSelectorValue = { periodValue: 12, periodYear: 2050 };
  private readonly subscription: Subscription = new Subscription();
  public marketQtdValue: QuarterSelectorValue | Date;
  public displayOverlay = true;
  private readonly element = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  public stateService = inject(RangeQuarterSelectorService);
  private readonly focusMonitor = inject(FocusMonitor);
  constructor() {
    effect(() => {
      const v = this.valueInput();
      if (v !== undefined) this.value = v;
    });
    effect(() => {
      // Track signal inputs that influence state
      this.marketQtdVal();
      this.maxValue();
      this.minValue();
      this.allowedPeriods();
      this.restrictedPeriods();
      this.singleSelection();
      this.requireYear();
      this.showAsQtd();
      this.type();
      if (this.initialRunDone) {
        this.onMaxValueHasData();
      }
    });
  }
  private initialRunDone = false;
  ngAfterViewInit() {
    this.focusMonitor
      .monitor(this.wrapperElement(), true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        if (origin && !this.isClearFilterEnabled()) {
          this.onElementClick();
        }
      });
  }
  ngOnInit() {
    this.onMaxValueHasData();
    this.initialRunDone = true;
  }
  ngOnDestroy() {
    // Required for untilDestroyed
    this.subscription.unsubscribe();
  }
  onElementClick() {
    if (!!this.editable() && this.displayOverlay) {
      this.createOverlay();
    }
    if (!this.displayOverlay) {
      this.displayValue = '';
      this.displayOverlay = true;
    }
  }
  private onMaxValueHasData() {
    if (this.marketQtdVal() || this.maxValue()) {
      this.marketQtdValue = this.marketQtdVal() || this.maxValue();
    }
    this.setNewStateFromOutside();
  }
  private setNewStateFromOutside() {
    this.processRestrictedAndAllowedPeriods();
    this.processMinAndMaxValues();
    const newState: QuarterRangeSelectorState = {
      selectorType: this.type(),
      requiredYear: this.requireYear(),
      ...this.value,
      minValue: this.minProcessedValue ? this.minProcessedValue : this.defaultMinValue,
      maxValue: this.maxProcessedValue ? this.maxProcessedValue : this.defaultMaxValue,
      singleSelection: this.singleSelection(),
      showAsQtd: this.showAsQtd(),
      allowedPeriods: this.allowThisPeriodsOnly,
      restrictedPeriods: this.restrictThisPeriodsOnly,
      marketQtdValue: this.marketQtdValue,
    };
    this.stateService.setInitialState(newState);
    // If the state is invalid, we change the reference.
    if (!this.stateService.isAValidState) {
      this.emitReference();
    }
    this.refreshLabel();
  }
  private processRestrictedAndAllowedPeriods() {
    const periodsToCheck: QuarterSelectorValue[] = [...(this.allowedPeriods() || []), ...(this.restrictedPeriods() || [])];
    const uniquePeriods = new Set();
    for (const period of periodsToCheck) {
      const keyToCheck = period.periodYear + '' + period.periodValue;
      if (uniquePeriods.has(keyToCheck)) {
        console.error(`Quarter selector: collision detected for period ${keyToCheck} `);
        this.allowThisPeriodsOnly = null;
        this.restrictThisPeriodsOnly = null;
        break;
      } else {
        uniquePeriods.add(keyToCheck);
      }
    }
    if (uniquePeriods.size === periodsToCheck.length) {
      const allowed = this.allowedPeriods() || [];
      const restricted = this.restrictedPeriods() || [];
      this.allowThisPeriodsOnly = allowed.length
        ? new Set(allowed.map(period => convertToSecretCode(period.periodYear, period.periodValue, this.type())))
        : null;
      this.restrictThisPeriodsOnly = restricted.length
        ? new Set(restricted.map(period => convertToSecretCode(period.periodYear, period.periodValue, this.type())))
        : null;
    }
  }
  private processMinAndMaxValues() {
    this.minProcessedValue = this.processLimitDate(this.minValue());
    this.maxProcessedValue = this.processLimitDate(this.maxValue());
  }
  private processLimitDate(date: Date | QuarterSelectorValue): QuarterSelectorValue {
    const isDate = date && typeof (date as Date).getTime === 'function';
    if (isDate) {
      const month = (date as Date).getMonth() + 1;
      if (this.type() === QuarterSelectorType.Monthly) {
        return {
          periodYear: (date as Date).getFullYear(),
          periodValue: month,
        };
      } else {
        const value = Math.ceil(month / 3);
        return {
          periodYear: (date as Date).getFullYear(),
          periodValue: value,
        };
      }
    } else {
      return date as QuarterSelectorValue;
    }
  }
  private createOverlay() {
    if (!this.isDialogOpen) {
      const overlayRef = this.overlay.create(this.getOverlayConfig());
      const componentToRender = new ComponentPortal(RangeQuarterSelectorComponent, null, this.injector);
      overlayRef.attach(componentToRender);
      this.subscription.add(
        overlayRef.backdropClick().subscribe(() => {
          this.closeOverlay(overlayRef);
        })
      );
      this.subscription.add(
        this.stateService.onFinishSelection().subscribe(() => {
          this.refreshLabel();
        })
      );
      this.subscription.add(
        this.stateService.onClose().subscribe(() => {
          this.closeOverlay(overlayRef);
        })
      );
      this.isDialogOpen = true;
    }
  }
  private closeOverlay(overlayRef) {
    overlayRef.dispose();
    this.isDialogOpen = false;
    this.refreshLabel();
    this.emitReference();
  }
  private refreshLabel() {
    this.displayValue = '';
    const state = this.stateService.getState();
    const stateIsEmpty = state.eq == null && state.lte == null && state.gte == null;
    if (this.stateService.isAValidState && stateIsEmpty) {
      this.displayValueSet = false;
      return;
    }
    if (state.eq != null || state.gte != null) {
      const value = state.eq || state.gte;
      const labeledValue = this.getLabeledValue(state.selectorType, value.periodValue);
      this.displayValue = `${labeledValue} ${this.requireYear() ? value.periodYear : ''}`;
    }
    this.whenLteIsNullAndValidIsMax(state);
    this.displayValueSet = true;
  }
  private whenLteIsNullAndValidIsMax(state: QuarterRangeSelectorState): void {
    if (state.lte != null && this.stateService.isMaxStateValid) {
      const lteLabeledValue = this.getLabeledValue(state.selectorType, state.lte.periodValue);
      const lteDisplay = `${lteLabeledValue} ${this.requireYear() ? state.lte.periodYear : ''}`;
      this.displayValue = `${this.displayValue} - ${lteDisplay}`;
    }
  }
  private getLabeledValue(selectorType: QuarterSelectorType, value: number): string {
    if (selectorType != QuarterSelectorType.Yearly) {
      let mapType: Map<number, string>;
      switch (selectorType) {
        case QuarterSelectorType.Quarter:
          mapType = QuarterLabels;
          break;
        case QuarterSelectorType.Monthly:
          mapType = MonthlyLabels;
          break;
        default:
          mapType = QuarterLabels;
      }
      if (mapType.has(value)) {
        return mapType.get(value);
      } else {
        console.error(`Incorrect value for the given type. value: ${value}, type: ${selectorType}`);
        return '';
      }
    }
    return '';
  }
  private emitReference() {
    if (this.stateService.isAValidState) {
      const { eq, lte, gte } = this.stateService.getState();
      this.value = { eq, lte, gte };
    } else {
      this.value = null;
    }
    this.valueChange.emit(this.value);
  }
  private getOverlayConfig() {
    const overlayConfiguration: OverlayConfig = {
      maxHeight: 160,
      width: 250,
      hasBackdrop: true,
    };
    overlayConfiguration.backdropClass = 'quarter-selector-backdrop-overlay';
    overlayConfiguration.positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.element)
      .withPositions([
        // bottom
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        // top
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);
    overlayConfiguration.scrollStrategy = this.overlay.scrollStrategies.block();
    return overlayConfiguration;
  }
  clearFilter() {
    this.displayOverlay = false;
    this.clearFilterEmitter.emit();
  }
}
